import { ReportOperationObject } from "@core/server/domain/request.interface";
import { addReportOperationApi } from "@core/server/utils/apis";
import {
  IrrigationSource,
  IrrigationSystem,
} from "@/features/irrigation-sources/domain/irrigation-sources.interface";
import {
  OperationReport,
  Product,
  Season,
} from "@/features/growing-season/domain/growing-season.interface";
import {
  AddGrowingSeasonArguments,
  EditGrowingSeasonArguments,
  GetGrowingSeasonsArguments,
  addGrowingSeason,
  editGrowingSeason,
  getGrowingSeasons,
  getGrowingSeasonById,
  getIrrigationSources,
  getIrrigationSystems,
  getProducts,
  GetOperationReportsByCategoryIdArguments,
  getOperationReportsByCategoryId,
  editReportOperation,
  DeleteReportOperationArguments,
  deleteReportOperation,
} from "@/features/growing-season/data/datasources/growing-season-datasources";

export interface GrowingSeasonRepository {
  _getProducts: () => Promise<Product[] | undefined>;
  _getIrrigationSources: () => Promise<IrrigationSource[] | undefined>;
  _getIrrigationSystems: () => Promise<IrrigationSystem[] | undefined>;
  _getGrowingSeason: (
    farmId: string,
    seasonId: string
  ) => Promise<Season | undefined>;
  _getAllGrowingSeasons: ({
    farmId,
    validGrowingSeasons,
  }: GetGrowingSeasonsArguments) => Promise<Season[] | undefined>;
  _addGrowingSeason: ({
    season,
    farmId,
  }: AddGrowingSeasonArguments) => Promise<Season | undefined>;
  _editGrowingSeason: ({
    season,
    farmId,
    seasonId,
  }: EditGrowingSeasonArguments) => Promise<Season | undefined>;
  _getOperationReportsByCategoryId: ({
    farmId,
    seasonId,
    categoryId,
  }: GetOperationReportsByCategoryIdArguments) => Promise<
    OperationReport[] | undefined
  >;
  _addReportOperation: ({
    farmId,
    seasonId,
    categoryId,
    typeId,
    data,
  }: ReportOperationObject) => Promise<string | undefined>;
  _editReportOperation: ({
    farmId,
    seasonId,
    categoryId,
    typeId,
    data,
    operationReportId,
  }: ReportOperationObject) => Promise<string | undefined>;
  _deleteReportOperation: ({
    farmId,
    seasonId,
    operationReportId,
  }: DeleteReportOperationArguments) => Promise<string | undefined>;
}

export class GrowingSeasonRepositoryImpl implements GrowingSeasonRepository {
  _getProducts = async (): Promise<Product[] | undefined> => {
    return await getProducts();
  };

  _getIrrigationSources = async (): Promise<IrrigationSource[] | undefined> => {
    return await getIrrigationSources();
  };

  _getIrrigationSystems = async (): Promise<IrrigationSystem[] | undefined> => {
    return await getIrrigationSystems();
  };

  _getGrowingSeason = async (
    farmId: string,
    seasonId: string
  ): Promise<Season | undefined> => {
    return await getGrowingSeasonById({ farmId, seasonId });
  };

  _getAllGrowingSeasons = async ({
    farmId,
    validGrowingSeasons,
  }: GetGrowingSeasonsArguments): Promise<Season[] | undefined> => {
    return await getGrowingSeasons({ farmId, validGrowingSeasons });
  };

  _addGrowingSeason = async ({
    season,
    farmId,
  }: AddGrowingSeasonArguments): Promise<Season | undefined> => {
    return await addGrowingSeason({ season, farmId });
  };

  _editGrowingSeason = async ({
    season,
    farmId,
    seasonId,
  }: EditGrowingSeasonArguments): Promise<Season | undefined> => {
    return await editGrowingSeason({ season, farmId, seasonId });
  };

  _getOperationReportsByCategoryId = async ({
    farmId,
    seasonId,
    categoryId,
  }: GetOperationReportsByCategoryIdArguments): Promise<
    OperationReport[] | undefined
  > => {
    return await getOperationReportsByCategoryId({
      farmId,
      seasonId,
      categoryId,
    });
  };

  _addReportOperation = async ({
    farmId,
    seasonId,
    categoryId,
    typeId,
    data,
  }: ReportOperationObject): Promise<string | undefined> => {
    return await addReportOperationApi({
      farmId,
      seasonId,
      categoryId,
      typeId,
      data,
    });
  };

  _editReportOperation = async ({
    farmId,
    seasonId,
    categoryId,
    typeId,
    data,
    operationReportId,
  }: ReportOperationObject): Promise<string | undefined> => {
    return await editReportOperation({
      farmId,
      seasonId,
      categoryId,
      typeId,
      data,
      operationReportId,
    });
  };

  _deleteReportOperation = async ({
    farmId,
    seasonId,
    operationReportId,
  }: DeleteReportOperationArguments): Promise<string | undefined> => {
    return await deleteReportOperation({ farmId, seasonId, operationReportId });
  };
}
